import { useMsal } from '@azure/msal-react';
import { Button, Text } from '@fluentui/react-components';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { useStores } from '../../common/hooks';
import { getUserName } from '../../common/string/get-user-name';
import { blankProgram, inactiveProgramStatus } from '../../models';
import { ConfirmationDialog, PageTitle } from '../shared-components';
import Loading from '../shared-components/Loading';
import { FlexBox } from '../shared-components/flex-box';
import './ProgramsPage.scss';
import { CreateProgramModal } from './components/CreateProgramModal';
import { ProgramsTable } from './components/ProgramsTable';

const ProgramsPageImpl: React.FC = () => {
  const {
    instance,
  } = useMsal();

  const {
    apiStore,
    programsPageUiStore,
    userStore,
  } = useStores();

  const [isLoading, setLoading] = useState<boolean>(false);

  const handleCreateProgram = async (programName: string): Promise<void> => {
    setLoading(true);
    if (!userStore.userInfo) {
      setLoading(false);
      return;
    }

    const newProgram = blankProgram();
    newProgram.displayName = programName;
    newProgram.owner = getUserName(
      userStore.userInfo.firstName,
      userStore.userInfo.lastName,
      userStore.userInfo.email,
    );

    try {
      const programRef = await apiStore.createProgram(instance, newProgram);
      newProgram.programId = programRef.programId;
      programsPageUiStore.addProgram(newProgram);
    } catch {
      // TODO: Handle error
    } finally {
      setLoading(false);
    }
  };

  const archiveProgram = async (): Promise<void> => {
    if (programsPageUiStore.programIdToArchive === undefined) return;

    setLoading(true);
    const inactiveStatus = inactiveProgramStatus();
    try {
      await apiStore.updateProgramStatus(instance, programsPageUiStore.programIdToArchive, inactiveStatus);
      programsPageUiStore.archiveProgram(programsPageUiStore.programIdToArchive);
    } catch {
      // TODO: Handle error
    } finally {
      setLoading(false);
      programsPageUiStore.setProgramIdToArchive(undefined);
    }
  };

  const preloadData = async (): Promise<void> => {
    setLoading(true);

    const programs = await apiStore.getPrograms(instance);
    programsPageUiStore.setPrograms(programs);

    setLoading(false);
  };

  useEffect(() => {
    preloadData();
    return () => {
      programsPageUiStore.resetStates();
    };
  }, []);

  return (
    <div className='programs-page'>
      {isLoading && <Loading />}
      <CreateProgramModal
        isModalOpen={programsPageUiStore.isShowCreateProgramModal}
        onSubmitModal={handleCreateProgram}
        onDismissModal={() => programsPageUiStore.setShowCreateProgramModal(false)}
      />
      {
        programsPageUiStore.isShowArchiveProgramModal &&
        <ConfirmationDialog
          isOpen
          dialogTitle='Archive'
          dialogSubtext='Are you sure you would like to archive this current program year?'
          primaryButtonLabel='Confirm'
          secondaryButtonLabel='Back'
          isShowCloseDialog={false}
          onPrimaryButtonClick={archiveProgram}
          onSecondaryButtonClick={() => programsPageUiStore.setProgramIdToArchive(undefined)}
        />
      }
      <FlexBox
        isFullWidth
        className='programs-page__header'
        aligns='end'
        content='space-between'
      >
        <PageTitle
          title='Programs'
          subtitle='Microsoft AI Cloud Partner Program'
        />

        <FlexBox
          direction='column'
          aligns='end'
          content='end'
        >
          <Button
            appearance='primary'
            onClick={() => programsPageUiStore.setShowCreateProgramModal(true)}
            disabled={programsPageUiStore.isShowCreateProgramErrorMessage}
          >
            Create new program
          </Button>
          {
            programsPageUiStore.isShowCreateProgramErrorMessage &&
            <Text
              size={200}
              className='text--red'
            >
              All programs should be archived before creating a new program.
            </Text>
          }
        </FlexBox>
      </FlexBox>
      <ProgramsTable />
    </div>
  );
};

export const ProgramsPage = observer(ProgramsPageImpl);